import { HttpClient } from '@angular/common/http';
import {AccountManagementProviderService} from '@modules/account-management/core/account-management-provider.service';
import {inject, Injectable} from '@angular/core';
import {DataEntity, OctopusConnectService} from 'octopus-connect';
import {AuthenticationService} from '@modules/authentication';
import {CommunicationCenterService} from '@modules/communication-center';
import {modulesSettings} from '../settings';
import {ModelSchema, Structures} from 'octopus-model';
import {clone, cloneDeep, get, groupBy, isEqual, merge, uniq, uniqBy} from 'lodash-es';
import {AuthorizationService} from '@modules/authorization';
import {combineLatest, Observable, of} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {DataCardInterface} from 'fuse-core/components/card/data-card.interface';
import {FreemiumPremiumService} from "shared/fremium-premium/freemium-premium.service";

const AUTONOMY_USAGE_LABEL = 'usage.autonomie';
const COLLECTIVE_USAGE_LABEL = 'usage.collectif';

// TODO faire autrement que de dupliquer les settings activities
const settingsStructure: ModelSchema = new ModelSchema({
    cardLayout: Structures.string('card-simple-course'),
    progress: Structures.boolean(),
    grade: Structures.boolean(),
    isPlayLessonActionIsLimitedToCollectiveAssignment: Structures.boolean(false),
    isLessonPreviewAccessible: Structures.object({default: false}),
});

@Injectable({
    providedIn: 'root'
})
export class DatacardService {

    userData: DataEntity;
    public settings: { [key: string]: any };
    private allowedRolesForModelsAssignation: string[] = [];
    private isAssignationFeatureActive: boolean;
    private freemiumPremiumService = inject(FreemiumPremiumService)

    constructor(
        public octopusConnect: OctopusConnectService,
        public http: HttpClient,
        public accountManagementProvider: AccountManagementProviderService,
        public authenticationService: AuthenticationService,
        public authorizationService: AuthorizationService,
        private communicationCenter: CommunicationCenterService,
    ) {
        this.communicationCenter
            .getRoom('authentication')
            .getSubject('userData')
            .subscribe((data: DataEntity) => {
                if (data) {
                    this.userData = data;
                } else {
                    this.userData = null;
                }

            });

        this.communicationCenter
            .getRoom('lessons')
            .getSubject('allowedRolesForModelsAssignation')
            .subscribe((data: string[]) => {
                this.allowedRolesForModelsAssignation = data ? data : [];
            });

        this.communicationCenter
            .getRoom('assignation')
            .getSubject('isAssignationActionIsActive')
            .subscribe((data: boolean) =>
                this.isAssignationFeatureActive = data
            );

        this.settings = settingsStructure.filterModel(modulesSettings.activities);
    }

    public checkAccess(user: string[]): boolean {
        if (user) {
            return this.authenticationService.hasLevel(user);
        }
        return false;
    }

    public isDuplicable(): boolean {
        // TODO pas en dur
        return this.checkAccess(['trainer', 'administrator', 'manager']);
    }

    public isByRole(type): boolean {
        return type === 'byRole';
    }

    public isManager(): boolean {
        return this.checkAccess(['manager']);
    }


    /**
     * Return true if type (model or not) of page and current user roles allow assignments
     * @param type if it's a model, value is 'byRole'
     * todo, c'est pas a ce service de gérer ça. C'est au parent de fournir les infos quand il passe les ressources.
     * todo le parent devrait appeler le service d'authorization qui sera en charge de faire ces tests
     */
    public isAssignable$(type, resource): Observable<boolean> {
        let assignationAllowed = false;

        if (this.isByRole(type)) {
            assignationAllowed = this.allowedRolesForModelsAssignation.includes(this.authenticationService.accessLevel);
        } else {
            assignationAllowed = this.checkAccess(['trainer', 'administrator']);
        }

        const allowedByUsages = this.isAssignmentAllowedByLessonUsage(resource, AUTONOMY_USAGE_LABEL);

        const currentUserCanAssign = this.authorizationService.currentUserCan<Observable<boolean>>('assignment.assign-lesson', undefined, resource);

        return combineLatest([
            currentUserCanAssign,
            of(allowedByUsages),
            of(assignationAllowed)
        ]).pipe(map(allMustBeTrue => allMustBeTrue.every(mustBeTrue => mustBeTrue === true)));
    }

    public processResources<T extends DataEntity>(resources: any[], callbacks: Partial<DataCardInterface<T>>, type, userSaves?: DataEntity[], options?: {
        [key: string]: any
    }): DataCardInterface<T>[] {
        return resources.map((resource) => {
            // inutile...
            const originalResource = resource;

            if (resource.ressourceEntity !== undefined) {
                resource = resource.ressourceEntity;
            }

            let dataCard: Partial<DataCardInterface<T>> = {
                'resource': resource,
                'originalResource': originalResource,
                'displayAssignedCount': !this.isByRole(type),
                'displayDuplicationCount': this.isByRole(type),
                'displayLesson': (_data) => false, // TODO a vérifier mais semble n'etre jamais a true
                'isAssignable$': this.isAssignable$(type, resource).pipe(filter(isAssignable => isAssignable && this.isAssignationFeatureActive)),
                'isDuplicable$': (_data) => of(this.isDuplicable()),
                isPreviewAccessible: this.isPreviewAccessible(),
                isPlayAccessible: this.isUsageAuthorizeToPlayLesson(resource),
                isQuizAccessible: options?.displayQuizBtnInCard,
                isQuizEnable: this.checkAccess(['trainer']) || (this.isQuizAccessible(resource, userSaves) && options?.displayQuizBtnInCard),
                isFreemiumPremiumLocked: this.isFreemiumPremiumLocked(),
                lastAssignment: options?.assignments ? this.getLastAssignment(resource, options.assignments) : undefined,
            };

            dataCard = merge(
                dataCard,
                callbacks,
                {
                    'playAlt': callbacks.play,
                    'download': callbacks.play
                }
            );

            return dataCard as DataCardInterface<T>;
        });
    }

    public isFreemiumPremiumLocked(): boolean {
        return this.freemiumPremiumService.isFreemiumPremiumFeatureEnabled() && this.freemiumPremiumService.isRouteAuthorized('lesson-edition/editor/') === false;
    }

    public getLastAssignment(resource: DataEntity, assignments: DataEntity[]): DataEntity {
        return assignments.find(assignment => +assignment.get('assignated_node')?.id === +resource?.id);
    }

    public isQuizAccessible(resource: DataEntity, userSaves: DataEntity[]): boolean {
        const isActivityDone = userSaves && userSaves.find((userSave) =>
            +userSave.get('granuleParent') === +resource.id
            && resource.get('reference') && +resource.get('reference')[0].id === +userSave.get('granule')[0]
            && userSave.get('state') === 'validated');
        return !!isActivityDone;
    }

    /***
     * download document and open in a new tab
     * @param path : path of the document
     */
    public downloadDoc(path: string): void {
        window.open(path, '_blank');
    }

    private isAssignmentAllowedByLessonUsage(resource: DataEntity, usage: string): boolean {
        return this.checkLessonUsageType(resource, usage);
    }

    private checkLessonUsageType(resource: DataEntity, usage: string): boolean {
        if (usage !== AUTONOMY_USAGE_LABEL && usage !== COLLECTIVE_USAGE_LABEL) {
            throw new Error('Type must be "en autonomie" or "collectif"');
        }

        const usages: { label: string }[] = get(resource, 'attributes.usage', []);
        return usages.length > 0 && usages.some(u => u.label === usage);
    }

    private isUsageAuthorizeToPlayLesson(resource: DataEntity): boolean {
        const isPlayLessonIsLimitedToCollectiveAssignment = this.settings.isPlayLessonActionIsLimitedToCollectiveAssignment;
        const isACollectiveAssignment = this.checkLessonUsageType(resource, COLLECTIVE_USAGE_LABEL);
        //ctz-setting
        const isLessonAssigned = this.settings?.isLessonAssignedUsePythonRule ? this.isLessonAssigned(resource) : false;
        //ctz-setting
        const isAccessPythonRules = this.settings?.isAccessPythonRules ? (!resource?.get('requirementsAccess') || resource?.get('requirementsAccess')?.access) : false;
        return (isAccessPythonRules || isLessonAssigned) || ((isPlayLessonIsLimitedToCollectiveAssignment && isACollectiveAssignment) || !isPlayLessonIsLimitedToCollectiveAssignment);
    }

    public isLessonAssigned(resource: DataEntity): boolean {
        return resource?.get('requirementsAccess')?.assigned;
    }


    private isPreviewAccessible() {
        const rawSettings = this.settings.isLessonPreviewAccessible;

        // si le role existe dans les settings, on prend la valeur du role
        if (this.authenticationService.accessLevel in rawSettings) {
            return rawSettings[this.authenticationService.accessLevel];
        }

        // sinon on prend la valeur par défaut
        return rawSettings.default || false;
    }
}
